<template>
  <pl-dialog
    :title="'Connect LMS'"
    :size="'lg'"
    :disableOutsideClick="true"
    @close="backToIntegrationsList()"
  >
    <div class="mt-5 px-7 flex font-semibold">
      <img :src="icon" class="rounded-full" style="height: 30px; width: 30px" />
      <span class="text-lg ml-3">
        {{
          provider.provider_name
            ? provider.provider_name
            : integration_connected.provider_name
        }}
      </span>
    </div>
    <div v-if="!students_tab" class="px-7">
      <h5 class="mt-3 mb-1 text-sm text-gray-500">
        Which {{ provider.provider_name }} class do you want to connect to
        {{ getCourseDetail.title }} in Parlay?
      </h5>
    </div>
    <div class="px-7 flex-grow overflow-y-auto">
      <template v-if="loading || import_students_loading">
        <div class="w-100 mt5 flex justify-center">
          <pl-spinner class="x1" />
        </div>
      </template>
      <template v-else>
        <div v-if="!students_tab" class="select-classroom">
          <pl-select
            :options="courses"
            label="name"
            :selected="selected_course"
            :arrow="true"
            :placeholder="'Select a Classroom'"
            @change="getCourseUsers($event)"
            class="mb-4"
          >
          </pl-select>
        </div>
        <div v-else>
          <div
            v-if="students && students.length"
            :key="$store.state.isReloadComponent"
          >
            <div class="my-3 text-base">Select Students</div>
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col">
                <p class="text-sm">Available</p>
                <div
                  class="w-full h-80 p-3 border border-gray-100 rounded overflow-auto"
                >
                  <div class="mb-2 bg-white border rounded border-gray-100">
                    <input
                      v-model="search_string_available"
                      class="p-2 text-sm placeholder-gray-200 w-full text-gray-500 bg-input"
                      type="text"
                      placeholder="Search..."
                    />
                  </div>
                  <div
                    v-if="sorted_available_list && sorted_available_list.length"
                    class="flex flex-nowrap"
                  >
                    <pl-checkbox
                      @change="handleSelectAll($event)"
                      v-model="is_all_selected"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500">
                      {{ is_all_selected ? 'De-select All' : 'Select All' }}
                    </span>
                  </div>
                  <div
                    v-for="student in sorted_available_list"
                    :key="student.id"
                    class="flex flex-nowrap my-2"
                  >
                    <pl-checkbox
                      @change="handleSelectStudent"
                      v-model="student.selected"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500">
                      {{ student.name }}
                    </span>
                  </div>
                </div>
                <button
                  @click="inviteStudents"
                  class="px-8 py-2 my-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
                  :disabled="!students_selected_for_invite_ids.length"
                >
                  Invite
                </button>
              </div>
              <div class="flex flex-col">
                <p class="text-sm">Invited</p>
                <div
                  class="w-full h-80 max-h-full p-3 border border-gray-100 rounded overflow-auto"
                >
                  <div class="mb-2 bg-white border rounded border-gray-100">
                    <input
                      v-model="search_string_invited"
                      class="p-2 text-sm placeholder-gray-200 w-full text-gray-500 bg-input"
                      type="text"
                      placeholder="Search..."
                    />
                  </div>
                  <div
                    class="flex flex-nowrap"
                    v-if="sorted_invited_list.length"
                  >
                    <pl-checkbox
                      @change="handleSelectAllUninvite($event)"
                      v-model="is_all_selected_invited"
                      checkBoxClass="checkmark-red"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500">
                      {{
                        is_all_selected_invited ? 'De-select All' : 'Select All'
                      }}
                    </span>
                  </div>
                  <div
                    v-for="(student, index) in sorted_invited_list"
                    :key="index"
                    class="flex flex-nowrap my-2"
                  >
                    <pl-checkbox
                      @change="handleSelectStudentUninvite"
                      v-model="student.selected"
                      checkBoxClass="checkmark-red"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500"> {{ student.name }} </span>
                  </div>
                </div>
                <button
                  @click="removeStudents"
                  class="px-8 py-2 my-2 bg-red-400 rounded cursor-pointer text-white font-semibold disabled:opacity-50"
                  :disabled="!students_selected_for_available_ids.length"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          <div v-else class="font-semibold">
            There are no students in the course you are trying to connect. Want
            to connect anyway? Click finish.
          </div>
        </div>
      </template>
    </div>
    <footer
      class="absolute bottom-0 w-full flex items-center justify-end py-5 px-7"
    >
      <div v-if="!students_tab" class="flex">
        <button
          v-if="!students_tab"
          @click="backToIntegrationsList()"
          class="px-8 py-2 hover:bg-gray-100 rounded cursor-pointer text-gray-500 font-semibold mr-2.5"
        >
          Cancel
        </button>
        <button
          v-if="!integration_connected || connected_without_choose_course"
          :disabled="!selected_course || import_students_loading"
          @click="inviteStudents"
          class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
        >
          Done
        </button>
      </div>
    </footer>
  </pl-dialog>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { EventBus } from 'utils/event-bus';

  export default {
    components: {},
    watch: {
      students_tab() {
        this.handleSelectAll(true);
      },
    },
    props: {
      courses: {
        type: Array,
        default() {
          return [];
        },
      },
      provider: {
        type: Object,
        default() {
          return {};
        },
      },
    },
    data() {
      return {
        edlink_course_id: '',
        edlink_course_name: '',
        students_tab: false,
        selected_course: null,
        is_all_selected: true,
        is_all_selected_invited: false,
        students: [],
        teachers: [],
        invited_list: [],
        remove_student_mode: false,
        loading: false,
        import_students_loading: false,
        search_string_available: '',
        search_string_invited: '',
      };
    },
    computed: {
      ...mapGetters(['getCourseDetail']),
      available_students_to_invite() {
        if (this.students && this.students.length) {
          return this.students.filter(
            (s) => !this.invited_list.map((i) => i.email).includes(s.email)
          );
        }
        return [];
      },
      sorted_available_list() {
        const results = this.available_students_to_invite.filter((x) =>
          x.name
            .toLowerCase()
            .includes(this.search_string_available.toLowerCase())
        );
        return results.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      },
      sorted_invited_list() {
        const results = this.invited_list.filter((x) =>
          x.name
            .toLowerCase()
            .includes(this.search_string_invited.toLowerCase())
        );
        return results.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      },
      students_selected_for_invite_ids() {
        return this.available_students_to_invite
          .filter((student) => student.selected === true)
          .map((s) => s.id);
      },
      students_selected_for_available_ids() {
        return this.invited_list
          .filter((student) => student.selected === true)
          .map((s) => s.id);
      },
      icon() {
        const title = this.provider.provider_name
          ? this.provider.provider_name
          : this.integration_connected.provider_name;
        return `/static/integrations/${title}.png`;
      },
      integration_connected() {
        if (
          this.getCourseDetail.edlink_integrations &&
          this.getCourseDetail.edlink_integrations.length
        ) {
          return this.getCourseDetail.edlink_integrations.find(
            (integration) =>
              integration.provider_name ===
              (this.provider.provider_name || this.provider.name)
          );
        }
        return false;
      },
      course_id_integration_connected() {
        return this.integration_connected &&
          this.integration_connected.course_id !== null
          ? this.integration_connected.course_id
          : null;
      },
      course_name_integration_connected() {
        return this.integration_connected &&
          this.integration_connected.course_name !== null
          ? this.integration_connected.course_name
          : null;
      },
      connected_without_choose_course() {
        return (
          this.integration_connected &&
          this.integration_connected.course_id === undefined
        );
      },
    },
    methods: {
      getCourseUsers(course) {
        this.selected_course = course;
        const integration_id =
          this.$route.query.integrationID ||
          this.provider.id ||
          this.provider.integration_id;
        const course_id = this.$route.params.courseID;
        const edlink_course_id =
          this.integration_connected && !this.connected_without_choose_course
            ? course.course_id
            : course.id;
        const edlink_course_name =
          this.integration_connected && !this.connected_without_choose_course
            ? course.course_name
            : course.name;
        const payload = {
          integration_id: integration_id,
          course_id: course_id,
          edlink_course_id,
        };
        this.edlink_course_id = edlink_course_id;
        this.edlink_course_name = edlink_course_name;
        this.getTeachersOfCourse(payload);
        this.getStudentsOfCourse(payload);
      },
      getTeachersOfCourse(payload) {
        this.$store
          .dispatch('getSsoCourseTeachers', payload)
          .then((res) => {
            this.teachers = res;
          })
          .catch((err) => console.error(err))
          .finally(() => {
            this.loading = false;
          });
      },

      getStudentsOfCourse(integration) {
        this.loading = true;
        const payload = {
          integration_id: integration.integration_id || integration.id,
          course_id: this.$route.params.courseID,
          edlink_course_id:
            integration.edlink_course_id || integration.course_id,
        };
        this.edlink_course_id = payload.edlink_course_id;
        this.$store
          .dispatch('getSsoCourseStudents', payload)
          .then((res) => {
            this.students = res.map((s) => {
              return { ...s, selected: true };
            });
            if (this.students && this.students.length) {
              this.invited_list = this.getCourseDetail.users.filter((u) => {
                return (
                  this.students.map((s) => s.email).includes(u.email) &&
                  u.subscription.edlink_integrations &&
                  u.subscription.edlink_integrations.find(
                    (x) =>
                      x.id ===
                      (this.provider.integration_id || this.provider.id)
                  )
                );
              });
              this.invited_list = this.invited_list.map((s) => {
                return { ...s, selected: false };
              });
            }
          })
          .catch((err) => console.error(err))
          .finally(() => {
            this.loading = false;
          });
      },
      handleSelectAll(status) {
        this.available_students_to_invite.map((s) => {
          s.selected = status;
        });
      },
      handleSelectAllUninvite(status) {
        this.invited_list.map((s) => {
          s.selected = status;
        });
      },
      handleSelectStudent() {
        this.is_all_selected =
          this.students_selected_for_invite_ids.length ===
          this.available_students_to_invite.length;
      },
      handleSelectStudentUninvite() {
        this.is_all_selected_invited =
          this.students_selected_for_available_ids.length ===
          this.invited_list.length;
      },
      inviteStudents() {
        this.import_students_loading = true;
        let array = [];
        const courseID = this.$route.params.courseID;
        const students_to_invite = this.available_students_to_invite.filter(
          (s) => this.students_selected_for_invite_ids.includes(s.id)
        );
        const integration_id =
          this.$route.query.integrationID ||
          this.provider.id ||
          this.provider.integration_id;
        students_to_invite.forEach((s) => {
          const student = {
            name: s.name,
            email: s.email,
            role: 'student',
            id: s.id,
          };
          array.push(student);
        });

        this.$store
          .dispatch('importSsoCourseStudents', {
            integration_id,
            course_id: courseID,
            edlink_course_id: this.edlink_course_id,
            edlink_course_name: this.edlink_course_name,
            students: array,
          })
          .then(async () => {
            await this.$store.dispatch('fetchCourseDetails', courseID);
            await this.$store.dispatch('getSsoCourseProviders', courseID);
            await this.getStudentsOfCourse(this.integration_connected);
            this.import_students_loading = false;
            const toast = {
              type: 'Success',
              message: 'Course invite list changed',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            this.$emit('close');
            if (
              this.$route.query.inviteModalOpen &&
              (this.$route.query.roundtableID ||
                this.$route.query.liveDiscussionID)
            ) {
              if (this.$route.query.roundtableID) {
                const route = {
                  name: 'roundtable.discussion',
                  params: {
                    courseID: this.$route.params.courseID,
                    roundtableID: this.$route.query.roundtableID,
                  },
                  query: {
                    inviteModalOpen: true,
                  },
                };
                this.$router.push(route);
              } else {
                const route = {
                  name: 'live-discussion.discussion',
                  params: {
                    courseID: this.$route.params.courseID,
                    liveDiscussionID: this.$route.query.liveDiscussionID,
                  },
                  query: {
                    inviteModalOpen: true,
                  },
                };
                this.$router.push(route);
              }
            }
          })
          .catch(() => {
            this.import_students_loading = false;
            const toast = {
              type: 'Error',
              message: 'Something went wrong, please try again.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          });
      },
      removeStudents() {
        const course_id = this.$route.params.courseID;
        const integration_id =
          this.$route.query.integrationID ||
          this.provider.id ||
          this.provider.integration_id;
        this.$store
          .dispatch('unsyncProviderStudentFromCourse', {
            course_id,
            integration_id,
            edlink_course_id: this.edlink_course_id,
            student_ids: this.students_selected_for_available_ids,
          })
          .then(async () => {
            await this.getStudentsOfCourse(this.integration_connected);
            this.remove_student_mode = false;
            const toast = {
              type: 'Success',
              message: 'Course invite list changed',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          })
          .catch(() => {
            this.import_students_loading = false;
            const toast = {
              type: 'Error',
              message: 'Something went wrong, please try again.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          });
      },
      updateRoster() {
        if (this.integration_connected) {
          if (!this.students.length) {
            this.getStudentsOfCourse(this.integration_connected);
          }
        }
      },
      backToIntegrationsList() {
        const course_id = this.$route.params.courseID;
        let route = '';
        if (
          this.$route.query.inviteModalOpen &&
          this.$route.query.roundtableID
        ) {
          route = {
            name: 'roundtable.index',
            params: {
              courseID: course_id,
              roundtableID: this.$route.query.roundtableID,
            },
            query: {
              inviteModalOpen: true,
            },
          };
        } else if (
          this.$route.query.inviteModalOpen &&
          this.$route.query.liveDiscussionID
        ) {
          route = {
            name: 'live-discussion.discussion',
            params: {
              courseID: course_id,
              liveDiscussionID: this.$route.query.liveDiscussionID,
            },
            query: {
              inviteModalOpen: true,
            },
          };
        } else {
          route = {
            name: 'course.settings.integrations',
            params: {
              courseID: course_id,
            },
            query: {},
          };
        }
        this.$router.replace(route);
        this.$emit('close');
      },
    },
    mounted() {
      this.students_tab =
        this.integration_connected &&
        this.integration_connected.course_id !== undefined;

      if (
        this.integration_connected &&
        this.integration_connected.course_id !== undefined
      ) {
        this.updateRoster();
      }
    },
  };
</script>
<style scoped lang="stylus">
  .nav-btn
    cursor: pointer
    color: #d8d8d8
    &.selected
      color: #72ccd7
  ::-webkit-scrollbar
    display: block
    width: 10px
    -webkit-appearance: none
</style>
<style lang="stylus">
  .select-classroom
    .custom-select
      padding: 0 !important
      .items
        max-height: calc(80vh - 300px) !important
      .selected
        padding: 10px !important
        border: 1px solid rgba(219, 223, 234, 1)
        border-radius: 5px
</style>



// WEBPACK FOOTER //
// integrationModal.vue?fd3c35a0